import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @param {Array<object>} variants @param {Array<string|null>} selection */
export function resolveCardVariant(variants, selection) {
  if (selection.some((value) => value === null)) return null;
  return variants.find((variant) => variant.options.length === selection.length && variant.options.every((value, index) => value === selection[index])) ?? null;
}

/** @param {Array<object>} variants @param {Array<string|null>} selection @param {number} index @param {string} value @param {number} colorIndex */
export function isCardChoiceAvailable(variants, selection, index, value, colorIndex) {
  return variants.some((variant) => variant.available && variant.options[index] === value &&
    (index === colorIndex || variant.options.every((option, position) => position === index || selection[position] === null || option === selection[position])));
}

/** Card selection; cart requests remain owned by the native product form. */
export class PawlCardOptions extends Component {
  #controller;
  #data;
  #busy = false;
  #pending;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', (event) => event.stopPropagation(), { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #initialize() {
    const source = this.querySelector('[data-pawl-variants]');
    if (!(source instanceof HTMLTemplateElement)) return;
    const optionCount = Number(this.dataset.optionCount);
    if (optionCount < 1 || optionCount > 2) return;
    const variants = Array.from(source.content.querySelectorAll('[data-pawl-variant]'), ({ dataset }) => ({
      id: dataset.id,
      options: [dataset.option1, dataset.option2].slice(0, optionCount),
      available: dataset.available === 'true',
      quantity: Number(dataset.quantity) || 1,
      price: dataset.price,
      compare: dataset.compare || null,
      image: dataset.image,
    }));
    if (!variants.length) return;
    const images = Object.fromEntries(Array.from(source.content.querySelectorAll('[data-pawl-image]'), ({ dataset }) => [dataset.id, {
      src: dataset.src,
      srcset: dataset.srcset,
      width: Number(dataset.width),
      height: Number(dataset.height),
      alt: dataset.alt,
    }]));
    this.#data = { optionCount, variants, images };
    this.#sync();
    for (const fieldset of this.querySelectorAll('fieldset')) fieldset.disabled = false;
    this.querySelector('product-form-component')?.removeAttribute('hidden');
    this.querySelector('[data-pawl-fallback]')?.setAttribute('hidden', '');
    this.setAttribute('data-pawl-ready', '');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.hasAttribute('data-pawl-option')) return;
    this.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
    this.#sync();
  };

  #sync() {
    if (!this.#data) return;
    const inputs = Array.from(this.querySelectorAll('input[data-pawl-option]'));
    const selection = Array(this.#data.optionCount).fill(null);
    for (const input of inputs) if (input.checked) selection[Number(input.dataset.pawlOption)] = input.value;
    const colorIndex = Number(this.dataset.colorIndex);
    for (const input of inputs) {
      const available = isCardChoiceAvailable(this.#data.variants, selection, Number(input.dataset.pawlOption), input.value, colorIndex);
      const label = available ? input.value : `${input.value} · ${this.dataset.soldOutLabel}`;
      input.dataset.pawlAvailable = String(available);
      input.disabled = this.#busy || !available;
      input.setAttribute('aria-label', label);
      input.closest('label')?.setAttribute('title', label);
    }
    for (const label of this.querySelectorAll('[data-pawl-value]')) {
      const value = selection[Number(label.dataset.pawlValue)];
      label.textContent = value ? `: ${value}` : '';
    }
    const variant = resolveCardVariant(this.#data.variants, selection);
    const representative = variant ?? this.#data.variants.find((candidate) => candidate.available &&
      (colorIndex < 0 || candidate.options[colorIndex] === selection[colorIndex]));
    const available = Boolean(variant?.available);
    const form = this.querySelector('product-form-component');
    const variantInput = form?.querySelector('input[name="id"]');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const button = form?.querySelector('button[type="submit"]');
    if (variantInput) {
      variantInput.value = available ? variant.id : '';
      variantInput.disabled = !available;
    }
    if (quantityInput) quantityInput.value = String(variant?.quantity || 1);
    if (form) form.dataset.quantityDefault = String(variant?.quantity || 1);
    if (button) button.disabled = this.#busy || !available;
    const complete = selection.every((value) => value !== null);
    const label = this.querySelector('[data-pawl-submit-label]');
    if (label) label.textContent = this.#busy ? this.dataset.busyLabel : !complete ? this.dataset.chooseLabel : available ? this.dataset.addLabel : this.dataset.unavailableLabel;
    const status = this.querySelector('[data-pawl-status]');
    if (status) status.textContent = complete && !available ? this.dataset.unavailableMessage : '';
    if (representative) this.#updateCard(representative);
  }

  /** @param {object} variant */
  #updateCard(variant) {
    const card = this.closest('product-card');
    if (!card) return;
    card.applyVariantToLinks?.(variant.id);
    const image = this.#data.images[variant.image];
    if (image) {
      const firstSlide = card.querySelector('.card-gallery slideshow-slide');
      const element = firstSlide?.querySelector('img') ?? card.querySelector('.card-gallery img');
      if (element instanceof HTMLImageElement && element.getAttribute('src') !== image.src) {
        element.src = image.src;
        element.srcset = image.srcset;
        element.width = image.width;
        element.height = image.height;
        element.alt = image.alt;
        card.setAttribute('data-featured-media-url', image.src);
      }
      if (firstSlide?.getAttribute('slide-id') && typeof card.refs?.slideshow?.select === 'function') {
        card.previewVariant?.(firstSlide.getAttribute('slide-id'));
      }
      const animation = this.querySelector('add-to-cart-component');
      if (animation) animation.dataset.productVariantMedia = image.src;
    }
    const container = card.querySelector('product-price [ref="priceContainer"]');
    if (!container) return;
    const price = container.querySelector('.price bdi');
    if (price) price.textContent = variant.price;
    const priceGroup = container.querySelector('.price');
    let priceLabel = priceGroup?.querySelector('.visually-hidden');
    if (priceGroup && !priceLabel && variant.compare) {
      priceLabel = document.createElement('span');
      priceLabel.className = 'visually-hidden';
      priceGroup.prepend(priceLabel);
    }
    if (priceLabel) priceLabel.textContent = `${variant.compare ? this.dataset.saleLabel : this.dataset.regularLabel} `;
    const comparison = container.querySelector('.compare-at-price');
    if (variant.compare) {
      const node = comparison ?? document.createElement('span');
      if (!comparison) {
        node.className = 'price-item__group compare-at-price';
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = `${this.dataset.regularLabel} `;
        node.append(label, document.createElement('bdi'));
        container.append(node);
      }
      node.querySelector('bdi').textContent = variant.compare;
    } else comparison?.remove();
  }

  /** @param {Event & {promise?: Promise<unknown>}} event */
  #onCartUpdate = (event) => {
    if (event.target !== this.querySelector('product-form-component') || !event.promise?.then) return;
    this.#pending = event.promise;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.#sync();
    const settle = () => {
      if (this.#pending !== event.promise) return;
      this.#busy = false;
      this.#pending = undefined;
      this.removeAttribute('aria-busy');
      if (this.isConnected) this.#sync();
    };
    event.promise.then(settle, settle);
  };
}

if (!customElements.get('pawl-card-options')) customElements.define('pawl-card-options', PawlCardOptions);
